// Spotlight — the dim-everything-except-one-element overlay used by the
// Part 2 feature reveals and the Part 3 AI finale.
//
// It resolves the first matching selector from `anchors`, cuts a lit hole
// around that element (a big box-shadow spread is the cheapest reliable
// cutout), and floats a tooltip card next to it — re-measuring on scroll
// and resize. When no anchor resolves (surface not mounted in this
// workspace, off-screen, etc.) it degrades to a centered card over a flat
// dim, so a missing target never blanks the tour.

import React from 'react';

const PAD = 8;               // breathing room around the highlighted element
const CARD_W = 340;
const GAP = 14;              // space between the hole and the card
const DIM = 'rgba(2, 6, 23, 0.72)';
const RING = 'rgba(139, 92, 246, 0.95)';

function findAnchorRect(anchors) {
  const list = Array.isArray(anchors) ? anchors : [];
  for (const sel of list) {
    let el = null;
    try { el = document.querySelector(sel); } catch { el = null; }
    if (el) {
      const r = el.getBoundingClientRect();
      // Ignore zero-size / detached nodes.
      if (r && r.width > 0 && r.height > 0) {
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      }
    }
  }
  return null;
}

export default function Spotlight({ anchors, children, zIndex = 70 }) {
  const [rect, setRect] = React.useState(null);
  const [cardSize, setCardSize] = React.useState({ w: CARD_W, h: 220 });
  const cardRef = React.useRef(null);
  const anchorsKey = React.useMemo(() => (Array.isArray(anchors) ? anchors.join('|') : ''), [anchors]);

  // Re-measure the anchor on mount, when the target set changes, and on any
  // scroll/resize. A short poll covers late-mounting targets (a tab that
  // just navigated in) without wiring into every view's render.
  React.useEffect(() => {
    let raf = 0;
    const measure = () => { setRect(findAnchorRect(anchors)); };
    measure();
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const poll = setInterval(measure, 400);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(poll);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [anchorsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useLayoutEffect(() => {
    if (cardRef.current) {
      const r = cardRef.current.getBoundingClientRect();
      if (r.height && (Math.abs(r.height - cardSize.h) > 2 || Math.abs(r.width - cardSize.w) > 2)) {
        setCardSize({ w: r.width, h: r.height });
      }
    }
  });

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  // Card placement: centered when there's no anchor; otherwise below the
  // hole if it fits, else above, else beside — always clamped on-screen.
  let cardStyle;
  if (!rect) {
    cardStyle = {
      position: 'fixed',
      left: Math.max(12, (vw - cardSize.w) / 2),
      top: Math.max(12, (vh - cardSize.h) / 2),
      width: CARD_W, maxWidth: 'calc(100vw - 24px)',
    };
  } else {
    const belowTop = rect.top + rect.height + GAP;
    const aboveTop = rect.top - cardSize.h - GAP;
    let top;
    if (belowTop + cardSize.h <= vh - 12) top = belowTop;
    else if (aboveTop >= 12) top = aboveTop;
    else top = Math.max(12, Math.min(rect.top, vh - cardSize.h - 12));
    let left = rect.left + rect.width / 2 - CARD_W / 2;
    left = Math.max(12, Math.min(left, vw - CARD_W - 12));
    cardStyle = { position: 'fixed', top, left, width: CARD_W, maxWidth: 'calc(100vw - 24px)' };
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex }} aria-live="polite">
      {/* Full-screen click absorber so stray clicks in the dimmed area
          don't fire underlying app buttons while the tour is up. */}
      <div
        style={{ position: 'absolute', inset: 0, background: rect ? 'transparent' : DIM }}
        onClick={(e) => e.stopPropagation()}
      />
      {rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 12,
            boxShadow: `0 0 0 9999px ${DIM}`,
            border: `2px solid ${RING}`,
            pointerEvents: 'none',
            transition: 'top 180ms ease, left 180ms ease, width 180ms ease, height 180ms ease',
          }}
        />
      )}
      <div ref={cardRef} style={cardStyle}>
        {children}
      </div>
    </div>
  );
}
